// @ts-strict-ignore
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { MaterialsModel } from '@3d-scene/service/materials.model';
import { Box3, EventDispatcher, Intersection, OrthographicCamera, Plane, Raycaster, Vector2, Vector3 } from 'three';
import { ArticleLineHolder } from '@article/helper/article-line-holder';
import { SelectionModel } from '@project/components/article/model/selection.model';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { Line } from '@3d-scene/model/line';
import { Dictionary } from 'typescript-collections';
import { ArticleUtils } from '@article/util/article-utils';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { ConnectionAreaVO, MetaElementUsageIdentityVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { RangeOnLineConnectionInfo } from '../model/range-on-line-connection-info';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { ProjectModel } from '../../../model/project.model';
import { UserModel } from '@user/model/user.model';
import { ProjectHelper } from '../../../helper/project.helper';
import { ConfiguratorScene } from '@3d-scene/scene/configurator-scene';
import { ProjectUtils } from '../../project/util/project-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { STATE } from '@3d-scene/common/scene/controls/camera-controls-constants';
import { UndoMoveArticlesOperation } from '@shared/undo/operation/undo-move-articles-operation';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { MoveArticlesBetweenBlocksData } from '@shared/undo/data/move-articles-between-blocks-data';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { NumberUtils } from '@shared/util/number-utils';
import { UndoCenterArticlesOperation } from '@shared/undo/operation/undo-center-articles-operation';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { Log } from '@shared/debug/log';
import { LabelEditorStateManager } from '../../label/label-editor/service/label-editor-state.manager';
import { LabelEditorMode } from '../../label/label-editor/label-editor.component';
import { CommonConstants } from '@3d-scene/constants/common-constants';
import { LayerManager } from '@3d-scene/manager/layer-manager';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { UnusedLabelingService } from '../../label/label-editor/service/unused-labeling.service';
import { GlobalContainer } from '@3d-scene/model3d/global-container';
import { Injectable } from '@angular/core';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { CarrierBlockManager } from './carrier-block.manager';
import { LineComponentUtils } from '@3d-scene/model3d/util/line-component-utils';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ReplaceLabelingArticleHelper } from '../../label/replace-labeling-helper/replace-labeling-article.helper';
import { Subject } from 'rxjs';
import { IOperation } from '@shared/undo/operation/i-operation';
import { auditTime } from 'rxjs/operators';
import { ReplaceLabelingUtils } from '../../label/replace-labeling-helper/replace-labeling-utils';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ReplaceBusbarUtils } from '@article/helper/replace/busbar/replace-busbar-utils';
import { SelectionModelService } from '../service/selection-model.service';
import { WireBridgeService } from './wire-bridge.service';
import { DeactivateProjectChangeActionService } from '@shared/service/deactivate-project-change-action.service';

export type ArticlesMovedHandlerData = {
  nonMovableArticlesMoved?: boolean,
  articleUsageIds: string[],
  moveArticlesMap?: Dictionary<string, Vector3>,
  articleDelta?: Vector3,
  articleOrderChanged: boolean,
}

export class MoveResult {
  private readonly _moveMap: Dictionary<string, Vector3>;

  constructor(moveMap: Dictionary<string, Vector3>) {
    this._moveMap = moveMap;
  }

  public get moveMap(): Dictionary<string, Vector3> {
    return this._moveMap;
  }
}

@Injectable({providedIn: 'root'})
export class ArticleMoveManager extends EventDispatcher {
  private static readonly REQUIRED_ARTICLES_COUNT_TO_REMOVE_SPACE: number = 2;
  private static readonly IS_RECURSIVE_INTERSECTION: boolean = true;

  private static readonly MIN_ARTICLE_WIDTH: number = 6;
  private static readonly DRAG_STUCK_DISTANCE: number = ArticleMoveManager.MIN_ARTICLE_WIDTH * 1.5 - CommonConstants.EPSILON;
  private static readonly DRAG_MAGNETIC_DISTANCE: number = 20;


  private _plane: Plane = new Plane();
  private _raycaster: Raycaster = new Raycaster();

  private _mousePosition: Vector2 = new Vector2();
  private _offset: Vector3 = new Vector3();
  private _intersection: Vector3 = new Vector3();

  private _targetArticleContainer: ArticleContainer = null;

  private _firstArticle: ArticleContainer;
  private _lastArticle: ArticleContainer;
  private _isMoveViewEnabled: boolean = false;
  private _movePath: Line = null;
  private _movedContainersLine: AdvancedLine = null;
  private _parentContainersToMove: ArticleContainer[] = null;
  private _parentContainersToMoveGuids: string[] = null;
  private _childContainersToMove: ArticleContainer[] = null;
  private _allContainersToMove: ArticleContainer[] = null;
  private _hiddenContainersOnRangeOnLine: ArticleContainer[] = null;
  private _targetArticlePosition: Vector3;
  private _insertArticleHelper: InsertArticleHelper = null;
  private _moveMap: Dictionary<string, Vector3> = null;
  private _isValid: boolean = true;
  private _additionalUndoOperations: IOperation[] = null;
  private _shiftLeft: boolean = false;
  private _projectedInitialPosition: Vector3 = null;

  private _documentMouseMoveSubject: Subject<MouseEvent> = new Subject<MouseEvent>();

  constructor(
    private userModel: UserModel,
    private globalContainer: GlobalContainer,
    private selectionModel: SelectionModel,
    private mountingRailHelper: MountingRailHelper,
    private sceneManager: SceneManager,
    private projectModel: ProjectModel,
    private projectMetaElementService: ProjectMetaElementService,
    private projectHelper: ProjectHelper,
    private undoRedoHelper: UndoRedoHelper,
    private labelEditorStateManager: LabelEditorStateManager,
    private layerManager: LayerManager,
    private projectContentPresenter: ProjectContentPresenter,
    private unusedLabelingService: UnusedLabelingService,
    private insertArticleUtils: InsertArticleUtils,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private containerService: ContainerService,
    private lineComponentUtils: LineComponentUtils,
    private articleContainerUtils: ArticleContainerUtils,
    private carrierBlockManager: CarrierBlockManager,
    private articleLineHolder: ArticleLineHolder,
    private replaceLabelingHelper: ReplaceLabelingArticleHelper,
    private replaceLabelingUtils: ReplaceLabelingUtils,
    private selectionModelService: SelectionModelService,
    private wireBridgeService: WireBridgeService,
    private configuratorScene: ConfiguratorScene,
    private materialsModel: MaterialsModel,
    private deactivateProjectChangeActionService: DeactivateProjectChangeActionService,
  ) {
    super();
    articleLineHolder.cacheEnabled = true;
  }

  private _onDocumentMouseMove: EventListener = (event: MouseEvent) => this.onDocumentMouseMove(event);
  private _onDocumentMouseCancel: EventListener = (event: MouseEvent) => this.onDocumentMouseCancel(event);
  private _onDocumentTouchMove: EventListener = (event: MouseEvent) => this.onDocumentMouseMove(event);
  private _onDocumentTouchEnd: EventListener = (event: MouseEvent) => this.onDocumentMouseCancel(event);

  public init(): void {
    this._documentMouseMoveSubject.pipe(auditTime(15)).subscribe((event: MouseEvent) => {
      this.onDocumentMouseHandler(event);
    });
  }

  private updateSceneObjectsVisibility(): void {
    if (this.configuratorScene.controls.hideSceneObjects === this._isMoveViewEnabled) {
      return;
    }
    this.toggleHiddenContainersVisibility();
    this.updateWireContoursOnMoveViewChanged();
    this.configuratorScene.controls.hiddenSceneObjects = this._isMoveViewEnabled ? this._allContainersToMove : null;
    this.configuratorScene.controls.hideSceneObjects = this._isMoveViewEnabled;
    if (!this.configuratorScene.controls.hideSceneObjects) {
      this.layerManager.deactivate('articlePosition');
    }
  }

  private toggleHiddenContainersVisibility(): void {
    this._hiddenContainersOnRangeOnLine?.forEach((container: ArticleContainer) => {
      container.toggleArticleVisibility();
    });
  }

  private updateWireContoursOnMoveViewChanged(): void {
    const selectedContainers: ArticleContainer[] = this.selectionModelService.getArticleContainers();
    selectedContainers.filter(ArrayUtils.notInArray, this._allContainersToMove).forEach((container: ArticleContainer) => {
      if(this._isMoveViewEnabled) container.unsetWireContourMaterial();
      else container.setWireContourMaterial(this.materialsModel.materialContourSelection);
    });
    this._allContainersToMove.filter(ArrayUtils.notInArray, selectedContainers).forEach((container: ArticleContainer) => {
      if(this._isMoveViewEnabled) container.setWireContourMaterial(this.materialsModel.materialContourSelection);
      else container.unsetWireContourMaterial();
    });
  }

  private updateWireContoursOnValidationChanged(isValid: boolean): void {
    this._allContainersToMove.forEach((container: ArticleContainer) => {
      container.setWireContourMaterial(isValid ? this.materialsModel.materialContourSelection : this.materialsModel.materialContourInvalid);
    });
  }

  public setArticleContainer(articleContainer: ArticleContainer, event: any): void {
    if (
      articleContainer &&
      articleContainer.articleSelected &&
      ArticleUtils.isArticleMovable(articleContainer.article) &&
      !ArticleUtils.isMountingRail(articleContainer.article) &&
      this.isEditPermissionExists() &&
      this.projectHelper.isMoveArticleAllowed(articleContainer) &&
      !this.projectModel.isAdminView
    ) {
      event.preventDefault();
      this.updateMousePosition(event);
      this.startArticleMove(articleContainer);
    }
  }

  private isEditPermissionExists(): boolean {
    return ProjectUtils.isProjectEditable(this.projectModel.currentProject, this.userModel.user);
  }

  private startArticleMove(articleContainer: ArticleContainer): void {
    if (this.deactivateProjectChangeActionService.shouldDeactivateProjectChangeAction()) {
      return;
    }
    this.addEventListeners();

    const cameraClone = this.getAndMoveCameraClone();
    this._raycaster.setFromCamera(this._mousePosition, cameraClone);

    const worldPosition: Vector3 = articleContainer.sceneTransformBounds.getCenter(new Vector3());
    this._plane.setFromNormalAndCoplanarPoint(cameraClone.getWorldDirection(this._plane.normal), worldPosition);
    const intersects: Intersection[] = this._raycaster.intersectObject(articleContainer, ArticleMoveManager.IS_RECURSIVE_INTERSECTION);
    if (intersects.length > 0) {
      this._targetArticleContainer = articleContainer;
      if (this._raycaster.ray.intersectPlane(this._plane, this._intersection)) {
        this._offset.copy(this._intersection).sub(this._targetArticleContainer.position);
      }
      this.disableSceneControls();
    }
  }

  private enableSceneControls(): void {
    this.removeEventListeners();
    this.configuratorScene.controls.enabled = true;
    this.configuratorScene.controls.setControlsState(STATE.NONE);
    this.updateScene();
  }

  private disableSceneControls(): void {
    this.configuratorScene.controls.enabled = false;
  }

  private updateScene(): void {
    this.configuratorScene.renderer.invalidate3DView();
  }

  public addEventListeners(): void {
    document.addEventListener('mousemove', this._onDocumentMouseMove, false);
    document.addEventListener('mouseup', this._onDocumentMouseCancel, false);
    document.addEventListener('mouseleave', this._onDocumentMouseCancel, false);
    document.addEventListener('touchmove', this._onDocumentTouchMove, false);
    document.addEventListener('touchend', this._onDocumentTouchEnd, false);
  }

  public removeEventListeners(): void {
    document.removeEventListener('mousemove', this._onDocumentMouseMove, false);
    document.removeEventListener('mouseup', this._onDocumentMouseCancel, false);
    document.removeEventListener('mouseleave', this._onDocumentMouseCancel, false);
    document.removeEventListener('touchmove', this._onDocumentTouchMove, false);
    document.removeEventListener('touchend', this._onDocumentTouchEnd, false);
  }

  public detachArticleContainerHandler(container: ArticleContainer): void {
    this.articleLineHolder.clearArticleLineCacheByGUIDs([container.articleGUID]);
  }

  public get isActive(): boolean {
    return this._isMoveViewEnabled;
  }

  public moveArticle(articleContainer: ArticleContainer, delta: Vector3): void {
    if (!articleContainer || !delta) {
      return;
    }
    const oldPosition: Vector3 = articleContainer.position.clone();
    articleContainer.moveTo(oldPosition.x + delta.x, oldPosition.y + delta.y, oldPosition.z + delta.z);
    this.articleLineHolder.moveArticleLine(articleContainer.articleGUID, delta);
    if (this._lastArticle && this._lastArticle === articleContainer) {
      this.showPositionLayer();
    }
  }

  public showPositionLayer(): void {
    if (this._firstArticle && this._lastArticle) {
      const firstArticlePositionX: number = this._firstArticle.sceneTransformBounds.max.x;
      const lastArticlePositionX: number = this._lastArticle.sceneTransformBounds.min.x;
      this.layerManager.layerDataHandlers.articlePosition.viewInfo = {
        leftPosition: this._firstArticle.position.clone().setX(firstArticlePositionX),
        rightPosition: this._lastArticle.position.clone().setX(lastArticlePositionX),
        isValid: this._isValid
      };

      if(!this.layerManager.isActive('articlePosition')) {
        this.layerManager.activate('articlePosition');
      } else {
        this.layerManager.updateLayer('articlePosition');
      }
    }
  }

  private createMoveMapByOffset(sourceArticles: ArticleContainer[], offset: Vector3): Dictionary<string, Vector3> {
    if (!sourceArticles || !offset) {
      return null;
    }
    const moveMap: Dictionary<string, Vector3> = new Dictionary<string, Vector3>();
    for (const article of sourceArticles) {
      moveMap.setValue(article.articleGUID, offset);
    }
    return moveMap;
  }

  public async createAndStoreMoveOperation(
    moveArticlesMap: Dictionary<string, Vector3>,
    rebuildProjectTree: boolean,
    nonMovableArticlesMoved: boolean,
    articleUsageIds: string[],
    articlesOrderChanged: boolean,
    moveArticlesBetweenBlocksDatas: MoveArticlesBetweenBlocksData[] = null
  ): Promise<void> {
    const undoMoveOperation: UndoMoveArticlesOperation = this.createUndoMoveArticlesOperation(
      moveArticlesMap,
      rebuildProjectTree,
      nonMovableArticlesMoved,
      articleUsageIds,
      articlesOrderChanged,
      moveArticlesBetweenBlocksDatas
    );
    if (ArrayUtils.isNotEmpty(this._additionalUndoOperations)) {
      undoMoveOperation.additionalOperations = this._additionalUndoOperations;
    }
    await this.undoRedoHelper.pushUndoOperation(undoMoveOperation);
  }

  private createUndoMoveArticlesOperation(
    moveArticlesMap: Dictionary<string, Vector3>,
    rebuildProjectTree: boolean,
    nonMovableArticlesMoved: boolean,
    articleUsageIds: string[],
    articlesOrderChanged: boolean,
    moveArticlesBetweenBlocksDatas: MoveArticlesBetweenBlocksData[]
  ): UndoMoveArticlesOperation {
    return new UndoMoveArticlesOperation(
      this.carrierBlockManager,
      this,
      this.layerManager,
      this.projectContentPresenter,
      this.unusedLabelingService,
      this.projectModel,
      this.insertArticleUtils,
      moveArticlesMap,
      true,
      rebuildProjectTree,
      nonMovableArticlesMoved,
      articleUsageIds,
      articlesOrderChanged,
      moveArticlesBetweenBlocksDatas
    );
  }

  private onDocumentMouseMove(event: MouseEvent): void {
    event.preventDefault();
    this._documentMouseMoveSubject.next(event);
  }

  private onDocumentMouseHandler(event: any) {
    try {
      this.move(event);
    } catch (error) {
      Log.error('Error occurred while trying to move article container: ', error);
      this.disableMoveView();
      this.resetWithTargetArticle();
      // If some error occurs it is important to remove event listeners.
      this.removeEventListeners();
      this.enableSceneControls();
    }
  }

  private async onDocumentMouseCancel(event: MouseEvent): Promise<void> {
    event.preventDefault();
    await this.stopMove();
    this.enableSceneControls();
  }

  public async stopMove(): Promise<void> {
    this.removeEventListeners();
    const isMoved: boolean = await this.executeMove();
    this.updateScene();
    this.disableMoveView();
    // creation of undo-operation (that could contain detach-operation in regard to strips on adapters)
    // should be called after the disableMoveView()-method to refresh connections correctly
    // (selection is disabled for invisible/hidden connections)
    // see ConnectionCube.refresh()
    if (isMoved) {
      const moveArticlesBetweenBlocksDatas: MoveArticlesBetweenBlocksData[] = this.carrierBlockManager.getMoveArticlesBetweenBlocksDatas(
        this._parentContainersToMove, this._insertArticleHelper, this._movedContainersLine);
      await this.createAndStoreMoveOperation(this._moveMap, true, false, this._moveMap.keys(), true, moveArticlesBetweenBlocksDatas);

      // In createAndStoreMoveOperation replaceLabelingHelper.createPendingLabelingArticles() is called,
      // which (in some cases?) restores missing labeling connections.
      // These changes are not notified in any way, so we need to force an update of the ProjectContentView.
      this.projectContentPresenter.articleContainerMovedHandler({articleOrderChanged: true, articleUsageIds: null});
    }
    this.resetWithTargetArticle();
  }

  private async executeMove(): Promise<boolean> {
    if (!this.isMoved()) {
      return false;
    }

    if (!this._isValid || !this._moveMap) {
      this.fullMoveArticle(this.currentArticleDelta.negate());
      this.updateWireContoursOnValidationChanged(true);
      return false;
    }

    const labelingContainers: ArticleContainer[] = this.replaceLabelingUtils.getLabelStripsToRecreateByMoveMap(this._movedContainersLine.start,
      this._moveMap, this._insertArticleHelper);
    if (CompositeArticleUtils.isCompositePartArticleExists(labelingContainers)) {
      return false;
    }
    const detachOperation: IOperation = await this.replaceLabelingHelper.detachLabelStrips(labelingContainers);
    if (detachOperation) {
      if (this._additionalUndoOperations) {
        this._additionalUndoOperations.push(detachOperation);
      } else {
        this._additionalUndoOperations = [detachOperation];
      }
    }
    this.insertArticleUtils.moveArticlesByMap(this._moveMap);
    for (const articleGUID of this._parentContainersToMoveGuids) {
      this._moveMap.setValue(articleGUID, this.currentArticleDelta);
    }
    const data: ArticlesMovedHandlerData = {
      articleOrderChanged: true,
      nonMovableArticlesMoved: false,
      articleUsageIds: this._moveMap.keys(),
      moveArticlesMap: this._moveMap,
    };
    this.executeMoveHandlers(data);

    return true;
  }

  private calculateMoveMap(): void {
    this._moveMap = this._insertArticleHelper.calculateMoveOffsetByArticleLine(this._movedContainersLine, null,
      this._parentContainersToMoveGuids, true, false, false, this._shiftLeft);
  }

  private validate(): void {
    const isValid: boolean = !!this._moveMap;
    if (this._isValid !== isValid) {
      this._isValid = isValid;
      this.updateWireContoursOnValidationChanged(isValid);
      this.showPositionLayer();
    }
  }

  /**
   * Returns true only if target article container was moved
   */
  private isMoved(): boolean {
    if (!this._targetArticlePosition || !this._targetArticleContainer) {
      return false;
    }
    return !MathUtils.equalsNumber3D(this._targetArticlePosition, this._targetArticleContainer.position);
  }

  private get currentArticleDelta(): Vector3 {
    return new Vector3(
      this._targetArticleContainer.position.x - this._targetArticlePosition.x,
      this._targetArticleContainer.position.y - this._targetArticlePosition.y,
      this._targetArticleContainer.position.z - this._targetArticlePosition.z
    );
  }

  private disableMoveView(): void {
    if (!this._isMoveViewEnabled) {
      return;
    }
    this._isMoveViewEnabled = false;
    this.updateSceneObjectsVisibility();
  }

  private resetWithTargetArticle(): void {
    this._targetArticlePosition = null;
    this._movePath = null;
    this._movedContainersLine = null;
    this._parentContainersToMove = null;
    this._parentContainersToMoveGuids = null;
    this._childContainersToMove = null;
    this._allContainersToMove = null;
    this._hiddenContainersOnRangeOnLine = null;
    this._isMoveViewEnabled = false;
    this._targetArticleContainer = null;
    this._insertArticleHelper = null;
    this._additionalUndoOperations = null;
    this._projectedInitialPosition = null;
  }

  private move(event: any): void {
    if (this.labelEditorStateManager.labelEditorMode === LabelEditorMode.TEXT_CHECK_FAILED) {
      return;
    }
    this.enableMoveView();
    this.updateMousePosition(event);

    this._raycaster.setFromCamera(this._mousePosition, this.getAndMoveCameraClone());

    if (this._targetArticleContainer && this._movePath) {
      if (this._raycaster.ray.intersectPlane(this._plane, this._intersection)) {
        const newPosition: Vector3 = this._intersection.sub(this._offset);
        if (!MathUtils.equalsNumber3D(this._targetArticleContainer.position, newPosition)) {
          const moveDelta: Vector3 = this.calculateMoveDelta(newPosition);
          this.fullMoveArticle(moveDelta);
          this.calculateMoveMap();
          this.validate();
          this.updateScene();
        }
      }
    }
  }

  private getAndMoveCameraClone(): OrthographicCamera {
    const cameraClone = this.configuratorScene.camera.clone() as OrthographicCamera;
    cameraClone.translateOnAxis(new Vector3(0, 0, 1), 20000);
    cameraClone.updateMatrixWorld(true);
    return cameraClone;
  }

  private fullMoveArticle(delta: Vector3): void {
    if (!MathUtils.equalsNumber3D(delta, new Vector3())) {
      // move basic article upLine
      this._movedContainersLine = this._movedContainersLine.move(delta) as AdvancedLine;
      // move article with linked children
      this.moveArticles(this._parentContainersToMove, delta);
      this.moveArticles(this._childContainersToMove, delta);
    }
  }

  private moveArticles(articles: ArticleContainer[], delta: Vector3): void {
    if (!articles) {
      return;
    }
    for (const article of articles) {
      this.moveArticle(article, delta);
    }
  }

  private calculateMoveDelta(newPosition: Vector3): Vector3 {
    const projectedNewPosition: Vector3 = this.findProjectedPoint(newPosition);
    const projectedCurrentPosition: Vector3 = this.findProjectedPoint(this._targetArticleContainer.position.clone());
    let delta: Vector3 = projectedNewPosition.clone().sub(projectedCurrentPosition);

    if (MathUtils.equalsNumber3D(delta, new Vector3())) {
      return new Vector3();
    }

    if (this._projectedInitialPosition === null) {
      this._projectedInitialPosition = projectedCurrentPosition.clone();
    }
    const mouseDelta: Vector3 = projectedNewPosition.clone().sub(this._projectedInitialPosition);
    const isMovingLeft: boolean = mouseDelta.x > 0;
    this._shiftLeft = !isMovingLeft;

    const newFigureLineStart: Vector3 = this._movedContainersLine.start.clone().add(delta);
    const newFigureLineEnd: Vector3 = this._movedContainersLine.end.clone().add(delta);
    if (!this._movePath.hasLine(new Line(newFigureLineStart, newFigureLineEnd))) {
      return this.getDeltaToFitInMoveLine(newFigureLineStart, newFigureLineEnd);
    }

    const deltaForClosePositions: Vector3 = this.getDeltaForCloseArticlePositions(isMovingLeft, newFigureLineStart, newFigureLineEnd);
    if (deltaForClosePositions) {
      return deltaForClosePositions;
    }

    if (isMovingLeft) {
      const leftArticle: ArticleContainer = this._insertArticleHelper.getLeftArticleContainerByPoint(newFigureLineStart, null,
        this._parentContainersToMoveGuids);
      if (leftArticle) {
        const leftArticleLine: AdvancedLine = this._insertArticleHelper.getArticleLine(leftArticle);
        if (leftArticleLine.hasPoint(newFigureLineStart)) {
          const maxOverlapLength: number = AdvancedLine.calculateMaxOverlapLength(leftArticleLine, this._movedContainersLine);
          const distance: number = newFigureLineStart.distanceTo(leftArticleLine.end) - maxOverlapLength;
          if (distance > 0 && distance < leftArticleLine.length / 2) {
            delta = leftArticleLine.end.clone().sub(this._movedContainersLine.start).add(new Vector3(maxOverlapLength, 0, 0));
          }
          if (distance > 0 && distance > leftArticleLine.length / 2) {
            delta = leftArticleLine.start.clone().sub(this._movedContainersLine.start);
          }
        }
      }
    } else {
      const rightArticle: ArticleContainer = this._insertArticleHelper.getRightArticleContainerByPoint(newFigureLineEnd, null,
        this._parentContainersToMoveGuids);
      if (rightArticle) {
        const rightArticleLine: AdvancedLine = this._insertArticleHelper.getArticleLine(rightArticle);
        if (rightArticleLine.hasPoint(newFigureLineEnd)) {
          const maxOverlapLength: number = AdvancedLine.calculateMaxOverlapLength(this._movedContainersLine, rightArticleLine);
          const distance: number = newFigureLineEnd.distanceTo(rightArticleLine.start) - maxOverlapLength;
          if (distance > 0 && distance < rightArticleLine.length / 2) {
            delta = rightArticleLine.start.clone().sub(this._movedContainersLine.end).add(new Vector3(-maxOverlapLength, 0, 0));
          }
          if (distance > 0 && distance > rightArticleLine.length / 2) {
            delta = rightArticleLine.end.clone().sub(this._movedContainersLine.end);
          }
        }
      }
    }

    return delta;
  }


  private getDeltaToFitInMoveLine(newFigureLineStart: Vector3, newFigureLineEnd: Vector3): Vector3 {
    const hasStartPoint: boolean = this._movePath.hasPoint(newFigureLineStart);
    const hasEndPoint: boolean = this._movePath.hasPoint(newFigureLineEnd);

    if (!hasStartPoint && !hasEndPoint) {
      if (this._movePath.closeToStart(newFigureLineStart)) {
        // correct delta to fit left side
        return this._movePath.start.clone().sub(this._movedContainersLine.start);
      } else {
        // correct delta to fit right side
        return this._movePath.end.clone().sub(this._movedContainersLine.end);
      }
    } else if (!hasStartPoint) {
      // correct delta to fit left side
      return this._movePath.start.clone().sub(this._movedContainersLine.start);
    } else if (!hasEndPoint) {
      // correct delta to fit left side
      return this._movePath.end.clone().sub(this._movedContainersLine.end);
    }
  }

  private getDeltaForCloseArticlePositions(isMovingLeft: boolean, newFigureLineStart: Vector3, newFigureLineEnd: Vector3): Vector3 {
    if (isMovingLeft) {
      const leftArticle: ArticleContainer = this._insertArticleHelper.getLeftArticleContainerByPoint(this._movedContainersLine.start);
      if (leftArticle) {
        const leftArticleLine: AdvancedLine = this._insertArticleHelper.getArticleLine(leftArticle);
        const maxOverlapLength: number = AdvancedLine.calculateMaxOverlapLength(leftArticleLine, this._movedContainersLine);
        const targetPoint: Vector3 = leftArticleLine.end.clone().add(new Vector3(maxOverlapLength, 0, 0));
        const isCurrentPointLeftOfTargetPoint: boolean = this._insertArticleHelper.getDistanceFromStart(newFigureLineStart) < this._insertArticleHelper.getDistanceFromStart(targetPoint);
        const distance: number = newFigureLineStart.distanceTo(targetPoint);
        if (isCurrentPointLeftOfTargetPoint) {
          // move containers close to the left article including possible overlaps
          if (distance < ArticleMoveManager.DRAG_STUCK_DISTANCE) {
            return targetPoint.sub(this._movedContainersLine.start);
          }
          // move containers to the left article start position to ensure the ability to take current article position
          if (newFigureLineStart.distanceTo(leftArticleLine.start) < ArticleMoveManager.DRAG_STUCK_DISTANCE) {
            return leftArticleLine.start.clone().sub(this._movedContainersLine.start);
          }
        } else if (distance < ArticleMoveManager.DRAG_MAGNETIC_DISTANCE) {
          // move containers close to the left article including possible overlaps
          return targetPoint.sub(this._movedContainersLine.start);
        }
      }
      // move containers to the left article start position in case "stuck distance" has been exceeded
      // to ensure the ability to take current article position
      const rightContainer: ArticleContainer = this._insertArticleHelper.getRightArticleContainerByPoint(newFigureLineStart, null,
        this._parentContainersToMoveGuids);
      const rightContainerLine: AdvancedLine = this._insertArticleHelper.getArticleLine(rightContainer);
      if (rightContainerLine && newFigureLineStart.distanceTo(rightContainerLine.start) < ArticleMoveManager.DRAG_STUCK_DISTANCE) {
        return rightContainerLine.start.clone().sub(this._movedContainersLine.start);
      }
    } else {
      const rightArticle: ArticleContainer = this._insertArticleHelper.getRightArticleContainerByPoint(this._movedContainersLine.end);
      if (rightArticle) {
        const rightArticleLine: AdvancedLine = this._insertArticleHelper.getArticleLine(rightArticle);
        const maxOverlapLength: number = AdvancedLine.calculateMaxOverlapLength(rightArticleLine, this._movedContainersLine);
        const targetPoint: Vector3 = rightArticleLine.start.clone().add(new Vector3(-maxOverlapLength, 0, 0));
        const isCurrentPointRightOfTargetPoint: boolean = this._insertArticleHelper.getDistanceFromStart(newFigureLineEnd) > this._insertArticleHelper.getDistanceFromStart(targetPoint);
        const distance: number = newFigureLineEnd.distanceTo(targetPoint);
        if (isCurrentPointRightOfTargetPoint) {
          // move containers close to the right article including possible overlaps
          if (distance < ArticleMoveManager.DRAG_STUCK_DISTANCE) {
            return targetPoint.sub(this._movedContainersLine.end);
          }
          // move containers to the right article end position to ensure the ability to take current article position
          if (newFigureLineEnd.distanceTo(rightArticleLine.end) < ArticleMoveManager.DRAG_STUCK_DISTANCE) {
            return rightArticleLine.end.clone().sub(this._movedContainersLine.end);
          }
        } else if (distance < ArticleMoveManager.DRAG_MAGNETIC_DISTANCE) {
          // move containers close to the right article including possible overlaps
          return targetPoint.sub(this._movedContainersLine.end);
        }
      }
      // move containers to the right article end position in case "stuck distance" has been exceeded
      // to ensure the ability to take current article position
      const leftContainer: ArticleContainer = this._insertArticleHelper.getLeftArticleContainerByPoint(newFigureLineEnd, null,
        this._parentContainersToMoveGuids);
      const leftContainerLine: AdvancedLine = this._insertArticleHelper.getArticleLine(leftContainer);
      if (leftContainerLine && newFigureLineEnd.distanceTo(leftContainerLine.end) < ArticleMoveManager.DRAG_STUCK_DISTANCE) {
        return leftContainerLine.end.clone().sub(this._movedContainersLine.end);
      }
    }
    return null;
  }

  private findProjectedPoint(point: Vector3): Vector3 {
    return MathUtils.closestPointOnLine(this._movePath.start, this._movePath.end, point);
  }

  private enableMoveView(): void {
    if (this._isMoveViewEnabled || !this._targetArticleContainer) {
      this.updateSceneObjectsVisibility();
      return;
    }
    this._targetArticlePosition = this._targetArticleContainer.position.clone();
    const acceptingAreaId: MetaElementUsageIdentityVO = this.getAcceptingAreaId();
    if (acceptingAreaId) {
      this._parentContainersToMove = this.findArticlesToMoveByAcceptingId(acceptingAreaId);
      this._parentContainersToMoveGuids = this.getArticleGuids(this._parentContainersToMove);
      this._childContainersToMove = this.articleContainerUtils.getAllConnectedArticlesRecursively(this._parentContainersToMove);
      this._allContainersToMove = [].concat(this._parentContainersToMove).concat(this._childContainersToMove);
      this._hiddenContainersOnRangeOnLine = this.findHiddenArticlesByAcceptingId(acceptingAreaId);
      this._movePath = this._insertArticleHelper.baseLine;
      this._movedContainersLine = this._insertArticleHelper.calculateCommonArticlesLine(this._parentContainersToMove);
      this._firstArticle = this._parentContainersToMove[0];
      this._lastArticle = this._parentContainersToMove[this._parentContainersToMove.length - 1];

      const rangeOnLineArticleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(this._insertArticleHelper.acceptingArticleUsageId);
      const articlesOnRail: ArticleContainer[] = this.containerService.getConnectedArticleContainersRecursively(rangeOnLineArticleContainer);
      const bbox: Box3 = ArticleContainerUtils.calculateBBoxForArticles(articlesOnRail);
      const railCenterPositionZ: number = rangeOnLineArticleContainer.sceneTransformBounds.getCenter(new Vector3()).z;
      const distanceFromTopToRailCenter: number = bbox.max.z - railCenterPositionZ;
      const distanceFromBottomToRailCenter: number = bbox.min.z - railCenterPositionZ;
      this.layerManager.layerDataHandlers.articlePosition.dimensions = {
        railCenterPositionZ,
        distanceFromTopToRailCenter,
        distanceFromBottomToRailCenter
      };
    }
      this.layerManager.updateLayer('articlePosition');
      this._isMoveViewEnabled = true;
    this.updateSceneObjectsVisibility();
  }

  private calculateDistanceForFreeSpace(articleLine: AdvancedLine, freeLine: AdvancedLine, isCalculationForStartLine: boolean = true): number {
    let freeDistance: number = 0;
    const overlapLength: number = AdvancedLine.calculateMaxOverlapLengthInInnerLine(freeLine, articleLine, isCalculationForStartLine);
    if (isCalculationForStartLine) {
      const freeStartPoint: Vector3 = MathUtils.getInterpolated(freeLine.start, freeLine.end, (freeLine.length + overlapLength) / freeLine.length);
      freeDistance = freeStartPoint.distanceTo(articleLine.start);
    } else {
      const freeEndPoint: Vector3 = MathUtils.getInterpolated(freeLine.end, freeLine.start, (freeLine.length + overlapLength) / freeLine.length);
      freeDistance = articleLine.end.distanceTo(freeEndPoint);
    }
    return freeDistance > CommonConstants.EPSILON ? freeDistance : 0;
  }

  private findArticlesToMoveByAcceptingId(acceptingAreaId: MetaElementUsageIdentityVO): ArticleContainer[] {
    const articleContainers: ArticleContainer[] = [];
    const articleContainersOnRangeOnLine: ArticleContainer[] = this.findArticlesBetweenSelectionByAcceptingId(acceptingAreaId);

    for (const articleContainer of articleContainersOnRangeOnLine) {
      if (this.articleContainerUtils.findArticleContainerByGUID(articleContainer.articleGUID, articleContainers) != null) {
        continue;
      }
      if (!articleContainer || !ArticleUtils.isArticleMovable(articleContainer.article)) {
        continue;
      }
      const combinedArticles: ArticleContainer[] = this.articleContainerUtils.findCombinedArticlesWithEndplates(
        articleContainer, true, true);
      for (const combinedArticle of combinedArticles) {
        articleContainers.push(combinedArticle);
      }
    }
    return this._insertArticleHelper.sortArticles(articleContainers);
  }

  private findArticlesBetweenSelectionByAcceptingId(acceptingAreaId: MetaElementUsageIdentityVO): ArticleContainer[] {
    const rangeOnLineArticleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(acceptingAreaId.articleUsageId);
    if (!rangeOnLineArticleContainer) {
      return [];
    }
    this._insertArticleHelper = this.insertArticleHelperFactory.createInstanceByRangeOnLineArticleContainer(rangeOnLineArticleContainer);
    const selectedArticlesOnRangeOnLine: ArticleContainer[] = this._insertArticleHelper.sortArticles(this.findSelectedArticlesByAcceptingId(acceptingAreaId));
    const firstArticle: ArticleContainer = selectedArticlesOnRangeOnLine[0];
    const lastArticle: ArticleContainer = selectedArticlesOnRangeOnLine[selectedArticlesOnRangeOnLine.length - 1];
    return this._insertArticleHelper.getConnectedArticlesBetweenContainers(
      firstArticle, lastArticle, null, true);
  }

  private findSelectedArticlesByAcceptingId(acceptingAreaId: MetaElementUsageIdentityVO): ArticleContainer[] {
    const articleContainers: ArticleContainer[] = [];
    const selectedUsageIds: string[] = this.selectionModel.getArticleUsageIds();
    for (const articleUsageId of selectedUsageIds) {

      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsageId);

      const parentIdentity: MetaElementUsageIdentityVO = ArticleUsageUtils.getFirstBackwardConnectedConnectionUsageId(articleContainer.articleUsage);
      if (acceptingAreaId.equals(parentIdentity)) {
        articleContainers.push(articleContainer);
      }
    }
    return articleContainers;
  }

  private findHiddenArticlesByAcceptingId(acceptingAreaId: MetaElementUsageIdentityVO): ArticleContainer[] {
    const hiddenArticleContainers: ArticleContainer[] = [];
    const rangeOnLineArticleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(acceptingAreaId.articleUsageId);
    if (rangeOnLineArticleContainer) {
      const articleUsageIds: string[] = ArticleUsageUtils.getAcceptingAreaIds(rangeOnLineArticleContainer.articleUsage);
      const articleContainersOnRangeOnLine: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(articleUsageIds);
      for (const articleContainer of articleContainersOnRangeOnLine) {
        if (!articleContainer.articleUsage.articleVisible && !ArticleUtils.isDrillingHole(articleContainer.article)) {
          hiddenArticleContainers.push(articleContainer);
        }
      }
    }
    return hiddenArticleContainers;
  }

  private getAcceptingAreaId(): MetaElementUsageIdentityVO {
    const parentAcceptingAreaIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(
      this._targetArticleContainer.articleUsage
    );
    if (parentAcceptingAreaIds.length === 1) {
      const identity: MetaElementUsageIdentityVO = parentAcceptingAreaIds[0];
      const metaElement: MetaElementVO = this.projectMetaElementService.getMetaElementByUsage(identity.articleUsageId, identity.metaElementName);
      if (ConnectionInfoUtils.isDefaultRangeOnLineConnection(metaElement)) {
        return identity;
      }
    }
    return null;
  }

  private updateMousePosition(event): void {
    const rect: DOMRect = this.configuratorScene.canvas.getBoundingClientRect();
    this._mousePosition.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this._mousePosition.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }

  // ***** methods below related to logic to remove free space between articles *****

  public async removeFreeSpaceBetweenArticlesHandler(articleContainers: ArticleContainer[]): Promise<void> {
    await this.removeFreeSpaceBetweenArticles(articleContainers);
  }

  public async removeFreeSpaceBetweenArticles(
    articleContainers: ArticleContainer[],
    storeUndoOperation: boolean = true,
    dispatchSuccessMessage: boolean = true,
    maxAllowedMoveOffsetMap: Dictionary<string, number> = null
  ): Promise<MoveResult> {
    if (!articleContainers) {
      return;
    }

    const onRailArticles: ArticleContainer[] = this.articleContainerUtils.filterArticlesConnectedToMountingRail(articleContainers, true);

    // We need at least two articles for this operation
    if (onRailArticles.length < ArticleMoveManager.REQUIRED_ARTICLES_COUNT_TO_REMOVE_SPACE) {
      return;
    }

    const insertArticleHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    const sortedArticles: ArticleContainer[] = insertArticleHelper.sortArticles(onRailArticles);
    const result: MoveResult = await this.removeSpaceBetweenTwoArticles(
      sortedArticles[0],
      sortedArticles[sortedArticles.length - 1],
      storeUndoOperation,
      dispatchSuccessMessage,
      maxAllowedMoveOffsetMap
    );

    this.sceneManager.setFocusAtCanvas();
    this.sceneManager.renderScene();

    return result;
  }

  private async removeSpaceBetweenTwoArticles(
    firstArticle: ArticleContainer,
    lastArticle: ArticleContainer,
    storeUndoOperation: boolean,
    dispatchSuccessMessage: boolean,
    maxAllowedMoveOffsetMap: Dictionary<string, number>
  ): Promise<MoveResult> {
    const blockingZoneLines: AdvancedLine[] = [];
    let processedContainers: string[] = [];
    const moveMapForUndoOperation: Dictionary<string, Vector3> = new Dictionary<string, Vector3>();
    const insertArticleHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    const firstArticleCombination: ArticleContainer[] = this.articleContainerUtils.findCombinedArticlesWithEndplates(firstArticle);
    const firstArticleLine: Line = insertArticleHelper.getArticleLine(firstArticle);
    const articlesToProcess: ArticleContainer[] = insertArticleHelper.getConnectedArticlesBetweenContainers(firstArticle, lastArticle);
    let placementPoint: Vector3 = firstArticleLine.end;
    const excludedGuids: string[] = this.getArticleGuids(firstArticleCombination);
    const articleIdsToProcess: string[] = [];

    for (const articleContainer of articlesToProcess) {
      if (processedContainers.indexOf(articleContainer.articleGUID) > -1) {
        continue;
      }
      articleIdsToProcess.push(articleContainer.articleGUID);
      if (!ArticleUtils.isArticleMovable(articleContainer.article)) {
        blockingZoneLines.push(insertArticleHelper.getArticleLine(articleContainer));
        continue;
      } else if (excludedGuids.indexOf(articleContainer.articleGUID) > -1) {
        placementPoint = insertArticleHelper.getArticleLine(articleContainer).end;
        continue;
      }

      let combinedArticles: ArticleContainer[] = this.articleContainerUtils.findCombinedArticlesWithEndplates(articleContainer);
      combinedArticles = insertArticleHelper.sortArticles(
        this.findAllIntersectedArticleCombinations(combinedArticles, insertArticleHelper, excludedGuids)
      );
      const containsNonMovableArticles: boolean = this.processContainersBetweenCombinedArticles(
        combinedArticles,
        insertArticleHelper,
        moveMapForUndoOperation,
        maxAllowedMoveOffsetMap
      );

      const commonLine: Line = insertArticleHelper.calculateCommonArticlesLine(combinedArticles);
      const guids: string[] = this.getArticleGuids(combinedArticles);

      if (
        containsNonMovableArticles ||
        insertArticleHelper.getDistanceFromStart(commonLine.start) < insertArticleHelper.getDistanceFromStart(placementPoint)
      ) {
        placementPoint = commonLine.end;
        continue;
      }

      placementPoint = this.findPlacementPoint(blockingZoneLines, commonLine, placementPoint, insertArticleHelper);

      const distanceFromStart: number = insertArticleHelper.getDistanceFromStart(placementPoint);
      const commonLineAfterMove: Line = insertArticleHelper.calculateArticleLine(distanceFromStart, commonLine.length);
      const moveOffset: Vector3 = commonLineAfterMove.end.clone().sub(commonLine.end);

      const maxAllowedOffset: number = ReplaceBusbarUtils.getMaxAllowedMoveOffset(maxAllowedMoveOffsetMap, combinedArticles);
      if (maxAllowedOffset < moveOffset.x) {
        moveOffset.setX(maxAllowedOffset);
      }

      this.moveArticlesByOffset(moveOffset, combinedArticles, moveMapForUndoOperation);
      ReplaceBusbarUtils.updateMaxAllowedMoveOffsetMap(maxAllowedMoveOffsetMap, combinedArticles, moveOffset.x);

      processedContainers = processedContainers.concat(guids);
      placementPoint = insertArticleHelper.calculateCommonArticlesLine(combinedArticles).end;
    }

    if (moveMapForUndoOperation && ArrayUtils.isNotEmpty(moveMapForUndoOperation.keys())) {
      if (storeUndoOperation) {
        await this.addUndoOperation(moveMapForUndoOperation, articleIdsToProcess);
      }
      if (dispatchSuccessMessage) {
        const data: ArticlesMovedHandlerData = {
          articleOrderChanged: true,
          nonMovableArticlesMoved: false,
          articleUsageIds: articleIdsToProcess,
        };
        this.executeMoveHandlers(data);
      }
    }

    return new MoveResult(moveMapForUndoOperation);
  }

  private findAllIntersectedArticleCombinations(
    combinedArticles: ArticleContainer[],
    insertArticleHelper: InsertArticleHelper,
    processedArticles: string[] = null
  ): ArticleContainer[] {
    if (combinedArticles.length < ArticleMoveManager.REQUIRED_ARTICLES_COUNT_TO_REMOVE_SPACE) {
      return combinedArticles;
    }

    let result: ArticleContainer[] = [];
    const guids: string[] = this.getArticleGuids(combinedArticles);
    const commonLine: AdvancedLine = insertArticleHelper.calculateCommonArticlesLine(combinedArticles);
    const containersBetweenCombinedArticles: ArticleContainer[] = insertArticleHelper.getConnectedArticlesFromLine(commonLine, guids);

    processedArticles = !processedArticles ? guids : processedArticles.concat(guids);
    result = result.concat(combinedArticles);

    if (containersBetweenCombinedArticles.length > 0) {
      for (const container of containersBetweenCombinedArticles) {
        if (processedArticles.indexOf(container.articleGUID) > -1) {
          continue;
        }

        if (commonLine.hasLine(insertArticleHelper.getArticleLine(container))) {
          result = result.concat(
            this.findAllIntersectedArticleCombinations(
              this.articleContainerUtils.findCombinedArticlesWithEndplates(container),
              insertArticleHelper,
              processedArticles
            )
          );
        }
      }
    }

    return result;
  }

  private processContainersBetweenCombinedArticles(
    articleContainers: ArticleContainer[],
    insertArticleHelper: InsertArticleHelper,
    moveMapForUndoOperation: Dictionary<string, Vector3>,
    maxAllowedMoveOffsetMap: Dictionary<string, number>
  ): boolean {
    if (!articleContainers || articleContainers.length <= 0) {
      return false;
    }

    let containsNonMovableArticles: boolean = false;
    let processedGuids: string[] = [];
    const mountingRailArticle: ArticleContainer = this.mountingRailHelper.getMountingRailContainer();
    const rangeOnLineConnection: ConnectionAreaVO = ConnectionInfoUtils.findDefaultRangeOnLineConnection(mountingRailArticle.article.metaElements);
    const metaElementUsageIdentity: MetaElementUsageIdentityVO = mountingRailArticle.findMetaElementUsageByName(rangeOnLineConnection.name).identity;

    for (const container of articleContainers) {
      if (processedGuids.indexOf(container.articleGUID) > -1) {
        continue;
      }

      if (!ArticleUtils.isArticleMovable(container.article)) {
        containsNonMovableArticles = true;
        continue;
      }

      const combinedArticles: ArticleContainer[] = this.articleContainerUtils.findCombinedArticlesWithEndplates(container);
      const excludedArticleGUIDs: string[] = this.getArticleGuids(combinedArticles);
      processedGuids = processedGuids.concat(excludedArticleGUIDs);

      let minLeftFreeSpace: number = Number.MAX_VALUE;
      let minMoveOffset: Vector3 = null;
      for (const containerToCheck of combinedArticles) {
        const rangeOnLineInfo: RangeOnLineConnectionInfo = new RangeOnLineConnectionInfo(
          this.containerService,
          this.lineComponentUtils,
          metaElementUsageIdentity,
          excludedArticleGUIDs
        );

        const articleLine: AdvancedLine = rangeOnLineInfo.getArticleLine(containerToCheck);
        const freeLine: AdvancedLine = rangeOnLineInfo.findFreeLineByArticleLine(articleLine);
        if (articleLine) {
          if (freeLine) {
            let leftFreeSpace: number = this.calculateDistanceForFreeSpace(articleLine, freeLine);

            const maxAllowedOffset: number = ReplaceBusbarUtils.getMaxAllowedMoveOffset(maxAllowedMoveOffsetMap, [containerToCheck]);
            if (maxAllowedOffset < leftFreeSpace) {
              leftFreeSpace = maxAllowedOffset;
            }

            if (minLeftFreeSpace > leftFreeSpace) {
              minLeftFreeSpace = leftFreeSpace;
              const distanceFromStart: number = insertArticleHelper.getDistanceFromStart(articleLine.start) - minLeftFreeSpace;
              const articleLineAfterMove: Line = insertArticleHelper.calculateArticleLine(distanceFromStart, articleLine.length);
              minMoveOffset = articleLineAfterMove.end.clone().sub(articleLine.end);
            }
          } else {
            minLeftFreeSpace = 0;
            minMoveOffset = null;
          }
        }
      }

      if (minMoveOffset) {
        this.moveArticlesByOffset(minMoveOffset, combinedArticles, moveMapForUndoOperation);
        ReplaceBusbarUtils.updateMaxAllowedMoveOffsetMap(maxAllowedMoveOffsetMap, combinedArticles, minMoveOffset.x);
      }
    }

    return containsNonMovableArticles;
  }

  private findPlacementPoint(
    blockingZoneLines: AdvancedLine[],
    commonLine: Line,
    previousPlacementPoint: Vector3,
    insertArticleHelper: InsertArticleHelper
  ): Vector3 {
    let result: Vector3 = previousPlacementPoint;
    let placementFound: boolean = false;
    while (!placementFound && blockingZoneLines.length > 0) {
      const blockingZoneLine: AdvancedLine = blockingZoneLines[0];
      if (insertArticleHelper.getDistanceFromStart(previousPlacementPoint) > insertArticleHelper.getDistanceFromStart(blockingZoneLine.start)) {
        blockingZoneLines.shift();
      } else if (commonLine.length <= previousPlacementPoint.distanceTo(blockingZoneLine.start)) {
        result = previousPlacementPoint;
        placementFound = true;
      } else {
        result = blockingZoneLine.end;
        blockingZoneLines.shift();
        placementFound = true;
      }
    }
    return result;
  }

  private moveArticlesByOffset(
    offset: Vector3,
    articleContainers: ArticleContainer[],
    moveMapForUndoOperation: Dictionary<string, Vector3> = null
  ): void {
    const moveMap: Dictionary<string, Vector3> = new Dictionary<string, Vector3>();
    for (const moveArticle of articleContainers) {
      moveMap.setValue(moveArticle.articleGUID, offset);
    }

    this.insertArticleUtils.moveArticlesByMap(moveMap);

    if (moveMapForUndoOperation) {
      InsertArticleUtils.mergeMoveMap(moveMapForUndoOperation, moveMap);
    }
  }

  private getArticleGuids(articleContainers: ArticleContainer[]): string[] {
    return ArticleContainerUtils.extractArticleGUIDs(articleContainers);
  }

  private async addUndoOperation(moveMap: Dictionary<string, Vector3>, articleUsageIds: string[]): Promise<void> {
    await this.createAndStoreMoveOperation(moveMap, true, false, articleUsageIds, false);
  }

  public async centerArticlesOnMountingRail(): Promise<void> {
    const insertHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    const articlesOnRail: ArticleContainer[] = insertHelper.getAllConnectedArticles();
    const removedBlockingZoneIds: string[] = this.removeFirstLastBlockingZones(articlesOnRail);
    if (ArrayUtils.isEmpty(articlesOnRail)) {
      return;
    }

    this.undoRedoHelper.startUndoOperationSequence();
    await this.undoRedoHelper.detachArticlesByUsageIds(removedBlockingZoneIds);

    const firstArticleOnRail: ArticleContainer = articlesOnRail[0];
    const firstArticleLine: Line = insertHelper.getArticleLine(firstArticleOnRail);
    const lastArticleOnRail: ArticleContainer = articlesOnRail[articlesOnRail.length - 1];
    const lastArticleLine: Line = insertHelper.getArticleLine(lastArticleOnRail);

    const currentStartPoint: Vector3 = firstArticleLine.start;
    const centeredStartOffset: number = (insertHelper.baseLine.length - firstArticleLine.start.distanceTo(lastArticleLine.end)) / 2;
    const centeredStartPoint: Vector3 = insertHelper.calculatePointByOffset(centeredStartOffset);
    if (!MathUtils.equalsNumber3D(currentStartPoint, centeredStartPoint)) {
      const moveMap: Dictionary<string, Vector3> = this.createMoveMapByOffset(articlesOnRail, centeredStartPoint.sub(currentStartPoint));
      const offsetMapForUndoOperation: Dictionary<string, number> = this.extractBlockingZonesOffset(articlesOnRail);
      this.insertArticleUtils.moveArticlesByMap(moveMap);
      const offsetMapForRedoOperation: Dictionary<string, number> = this.updateBlockingZonesOffset(articlesOnRail, insertHelper);
      await this.undoRedoHelper.pushUndoOperation(
        new UndoCenterArticlesOperation(
          moveMap,
          true,
          this.insertArticleUtils,
          this.carrierBlockManager,
          this,
          this.layerManager,
          this.projectContentPresenter,
          this.unusedLabelingService,
          this.projectModel,
          offsetMapForUndoOperation,
          offsetMapForRedoOperation,
          this.containerService
        )
      );
    }

    this.undoRedoHelper.finalizeUndoOperationSequence();
    const data: ArticlesMovedHandlerData = {
      articleOrderChanged: false,
      nonMovableArticlesMoved: true,
      articleUsageIds: null,
    };
    this.executeMoveHandlers(data);
  }

  private extractBlockingZonesOffset(articlesOnRail: ArticleContainer[]): Dictionary<string, number> {
    const guidToOffsetMap: Dictionary<string, number> = new Dictionary<string, number>();
    for (const article of articlesOnRail) {
      if (ArticleUtils.isArticleMovable(article.article)) {
        continue;
      }
      const offset: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
        article.articleUsage,
        ArticleUsageInfoPropertyDef.ARTICLE_OFFSET
      );
      if (NumberUtils.isNumber(offset)) {
        guidToOffsetMap.setValue(article.articleUsage.id, offset);
      }
    }
    return guidToOffsetMap;
  }

  private updateBlockingZonesOffset(articlesOnRail: ArticleContainer[], insertHelper: InsertArticleHelper): Dictionary<string, number> {
    const guidToOffsetMap: Dictionary<string, number> = new Dictionary<string, number>();
    for (const article of articlesOnRail) {
      if (ArticleUtils.isArticleMovable(article.article)) {
        continue;
      }
      const offset: number = insertHelper.calculateArticleOffset(article.articleUsage.id, this.globalContainer);
      ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(article.articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_OFFSET, offset);
      guidToOffsetMap.setValue(article.articleUsage.id, offset);
    }
    return guidToOffsetMap;
  }

  /**
   * @return array of usage ids of blocking zones removed from source array
   */
  private removeFirstLastBlockingZones(sortedArticlesOnRail: ArticleContainer[]): string[] {
    if (ArrayUtils.isEmpty(sortedArticlesOnRail)) {
      return [];
    }
    const removedBlockingZoneUsageIds: string[] = [];
    let articleToCheck: ArticleContainer = null;
    for (let i = 0; i < sortedArticlesOnRail.length; i++) {
      articleToCheck = sortedArticlesOnRail[i];
      if (!ArticleUtils.isBlockingZone(articleToCheck.article)) {
        break;
      }
      removedBlockingZoneUsageIds.push(articleToCheck.articleGUID);
      sortedArticlesOnRail.splice(i, 1);
      i--;
    }
    for (let j = sortedArticlesOnRail.length - 1; j >= 0; j--) {
      articleToCheck = sortedArticlesOnRail[j];
      if (!ArticleUtils.isBlockingZone(articleToCheck.article)) {
        break;
      }
      removedBlockingZoneUsageIds.push(articleToCheck.articleGUID);
      sortedArticlesOnRail.splice(j, 1);
    }
    return removedBlockingZoneUsageIds;
  }

  public articleContainerMovedHandler(data: ArticlesMovedHandlerData): void {
    if (data && !data.nonMovableArticlesMoved && ArrayUtils.isNotEmpty(data.articleUsageIds)) {
      const containers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(data.articleUsageIds);
      for (const container of containers) {
        if (ArticleUtils.isCoverLabelStripArticle(container.article)) {
          const usage: ArticleUsageVO = container.articleUsage;
          const insertHelper: InsertArticleHelper = this.insertArticleHelperFactory.createInstanceByConnectedArticleUsage(usage);
          if (insertHelper) {
            const offsetValue: number = insertHelper.calculateArticleOffset(usage.id, this.globalContainer);
            ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(usage, ArticleUsageInfoPropertyDef.ARTICLE_OFFSET, offsetValue);
          }
        }
      }
     this.wireBridgeService.updateWireBridgeObjects(
        this._childContainersToMove ??
        this.articleContainerUtils.getAllConnectedArticlesRecursively(containers));
    }
  }

  private executeMoveHandlers(data: ArticlesMovedHandlerData): void {
    this.layerManager.queueUpdate();
    this.projectModel.projectChangeHandler();
    this.unusedLabelingService.articleContainerMovedHandler(data);
    this.articleContainerMovedHandler(data);
    this.projectContentPresenter.articleContainerMovedHandler(data);
  }
}
